/**
 * WW Plans Compare — dialog behaviour for the Section 9 "Compare plans" table
 * Author: Plaud Tayler — 2026-09-08
 * Version: 1.1.0 - 2026-09-28 (UTC+8) - DTC-1346 — overlay pill scrollbar for the desktop table, bound only where the section's Note Pro Flow style renders [data-wplc-sbar]
 * Version-note: 1.0.2 - 2026-09-08 15:15 (UTC+8) - Plaud Tayler — initial focus taken after a forced style flush rather than on rAF, which never fires in a hidden tab
 * Version-note: 1.0.0 - 2026-09-08 14:35 (UTC+8) - Plaud Tayler — initial
 *
 * Reuse note. product-compare-popup was evaluated first and its DOM/JS is not reusable here: it
 * compares DEVICES from compare_tabs / compare_devices block settings through a Swiper card track
 * and a tab bar, while this is a frozen-header feature matrix. It is also rendered by five product
 * sections, so reshaping it to carry plans would put those at risk for no gain.
 * What IS reused is the theme's actual shared piece — window.A11yFocusUtils.trapFocus from
 * accessibility-fix.js, the same utility that component uses — plus the same dialog contract:
 * ESC closes, the scrim closes, the body scroll is locked with its position restored, focus is
 * trapped while open and handed back to the trigger on close.
 *
 * Progressive enhancement: with this file absent the dialog markup stays inert and hidden, and the
 * trigger is a plain button that does nothing rather than a broken link.
 */
(function () {
  'use strict';
  if (window.__wwPlansCompare) return;
  window.__wwPlansCompare = true;

  var scrollY = 0;
  var locks = 0;

  function lock() {
    if (locks++) return;
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.width = '100%';
  }

  function unlock() {
    if (--locks > 0) return;
    locks = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  function Dialog(root) {
    this.root = root;
    this.overlay = root.querySelector('[data-wplc-overlay]');
    this.trigger = null;
    this.untrap = null;
    this.onKey = this.handleKey.bind(this);
  }

  Dialog.prototype.open = function (trigger) {
    if (!this.overlay || this.overlay.classList.contains('is-open')) return;
    this.trigger = trigger || null;
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.overlay.removeAttribute('inert');
    lock();
    var sb = this.overlay.querySelector('[data-wplc-sbar]');
    if (sb && sb.__wplcSbar) sb.__wplcSbar.sync();
    document.addEventListener('keydown', this.onKey);
    var dialog = this.overlay.querySelector('[role="dialog"]') || this.overlay;
    if (window.A11yFocusUtils && window.A11yFocusUtils.trapFocus) {
      this.untrap = window.A11yFocusUtils.trapFocus(dialog);
      document.addEventListener('keydown', this.untrap, true);
    }
    /* The overlay has only just gained .is-open, so its computed visibility is still `hidden` and
       focus() on anything inside it would be a no-op. Force the style flush by reading a layout
       property, then focus synchronously — rAF would also work in a visible tab but never fires in
       a hidden one, which would leave the dialog open with focus stranded on the trigger. */
    var close = this.overlay.querySelector('[data-wplc-close]');
    if (close) {
      void this.overlay.offsetWidth;
      close.focus();
    }
    /* DTC-1346: the Note Pro Flow style shows the phone sheet up to 1024, so a page loaded at >=1025
       (sheet not displayed, both arrows computed as disabled) can reach it by rotating or resizing.
       Re-read the rail ends on open. Scoped to that style; other instances are untouched. */
    if (this.root.closest && this.root.closest('.ww-plans--notepro-flow')) syncRailNav(this.root);
  };

  Dialog.prototype.close = function () {
    if (!this.overlay || !this.overlay.classList.contains('is-open')) return;
    this.overlay.classList.remove('is-open');
    unlock();
    document.removeEventListener('keydown', this.onKey);
    if (this.untrap) {
      document.removeEventListener('keydown', this.untrap, true);
      this.untrap = null;
    }
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('inert', '');
    if (this.trigger) { this.trigger.focus(); this.trigger = null; }
  };

  Dialog.prototype.handleKey = function (e) {
    if (e.key === 'Escape') this.close();
  };

  function dialogFor(el) {
    if (!el) return null;
    if (!el.__wplc) el.__wplc = new Dialog(el);
    return el.__wplc;
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-wplc-open]');
    if (t) {
      var root = document.getElementById(t.getAttribute('data-wplc-open'));
      var d = dialogFor(root);
      if (d) { e.preventDefault(); d.open(t); }
      return;
    }
    var c = e.target.closest && e.target.closest('[data-wplc-close]');
    if (c) {
      var d2 = dialogFor(c.closest('ww-plans-compare'));
      if (d2) d2.close();
      return;
    }
    /* the scrim itself, not anything inside the dialog */
    if (e.target.matches && e.target.matches('[data-wplc-overlay]')) {
      var d3 = dialogFor(e.target.closest('ww-plans-compare'));
      if (d3) d3.close();
      return;
    }

    /* The phone dialog's rails - Plaud Tayler - 2026-09-14 14:05 - Begin
       1545:37967 puts one pair of arrows under four rails, so a step moves all of them: the rails
       are four readings of the same feature order and letting them drift apart would leave the
       reader comparing row 3 of one plan against row 1 of another. Touch still scrolls a rail on
       its own - this only drives the buttons. */
    var nav = e.target.closest && e.target.closest('[data-wplc-rail-nav]');
    if (nav) {
      var scope = nav.closest('ww-plans-compare');
      if (!scope) return;
      var rails = scope.querySelectorAll('[data-wplc-rail]');
      if (!rails.length) return;
      var first = rails[0];
      var cell = first.querySelector('.wplc__gcell');
      var step = cell ? cell.getBoundingClientRect().width : 150;
      var dir = nav.getAttribute('data-wplc-rail-nav') === 'prev' ? -1 : 1;
      var to = first.scrollLeft + dir * step;
      /* Rule 6: the step itself is the point, the glide is not — under reduce it jumps. */
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      for (var i = 0; i < rails.length; i++) {
        rails[i].scrollTo({ left: to, behavior: reduce ? 'auto' : 'smooth' });
      }
    }
  });

  /* Both buttons read their state off the first rail: it is the one the step is computed from, and
     with every rail holding the same number of cells the four ends coincide. */
  function syncRailNav(scope) {
    var rail = scope.querySelector('[data-wplc-rail]');
    var btns = scope.querySelectorAll('[data-wplc-rail-nav]');
    if (!rail || !btns.length) return;
    var max = rail.scrollWidth - rail.clientWidth;
    for (var i = 0; i < btns.length; i++) {
      var prev = btns[i].getAttribute('data-wplc-rail-nav') === 'prev';
      btns[i].disabled = prev ? rail.scrollLeft <= 1 : rail.scrollLeft >= max - 1;
    }
  }

  document.addEventListener('scroll', function (e) {
    var rail = e.target;
    if (!rail || !rail.hasAttribute || !rail.hasAttribute('data-wplc-rail')) return;
    var scope = rail.closest('ww-plans-compare');
    if (scope) syncRailNav(scope);
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    var scopes = document.querySelectorAll('ww-plans-compare');
    for (var i = 0; i < scopes.length; i++) syncRailNav(scopes[i]);
  });
  /* The phone dialog's rails - Plaud Tayler - 2026-09-14 14:05 - End */

  /* Overlay scrollbar for the desktop table - DTC-1346 - Begin
     Only a section with the Note Pro Flow style renders [data-wplc-sbar], so nothing here runs for
     any other instance. The comp's pill has an outer shadow that a ::-webkit-scrollbar thumb cannot
     paint, so CSS hides the native bar and this draws the pill over the scroll box: sized from
     clientHeight / scrollHeight, moved on scroll, draggable. Wheel, touch and keyboard scrolling
     stay native. The track is inset 8 at the top and 16 at the foot so the pill clears the sheet's
     rounded corner. */
  var SBAR_TOP = 8;
  var SBAR_FOOT = 16;
  var SBAR_MIN = 40;

  function Sbar(bar) {
    this.bar = bar;
    this.thumb = bar.querySelector('[data-wplc-sbar-thumb]');
    this.box = bar.parentNode && bar.parentNode.querySelector('.wplc__scroll');
    if (!this.thumb || !this.box) return;
    var self = this;
    var update = function () { self.sync(); };
    this.box.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (typeof ResizeObserver === 'function') new ResizeObserver(update).observe(this.box);
    this.thumb.addEventListener('pointerdown', function (e) { self.drag(e); });
    this.sync();
  }

  Sbar.prototype.sync = function () {
    var box = this.box;
    if (!box) return;
    var view = box.clientHeight;
    var full = box.scrollHeight;
    var track = view - SBAR_TOP - SBAR_FOOT;
    /* Nothing to scroll, or the table is not displayed (the phone sheet below 1025). */
    if (track <= 0 || full - view < 1) { this.bar.hidden = true; return; }
    this.bar.hidden = false;
    this.bar.style.top = (box.offsetTop + SBAR_TOP) + 'px';
    this.bar.style.height = track + 'px';
    var h = Math.max(SBAR_MIN, Math.round(track * view / full));
    var y = Math.round(box.scrollTop / (full - view) * (track - h));
    this.thumb.style.height = h + 'px';
    this.thumb.style.transform = 'translateY(' + y + 'px)';
  };

  Sbar.prototype.drag = function (e) {
    if (e.button !== undefined && e.button !== 0) return;
    var box = this.box;
    var thumb = this.thumb;
    var track = this.bar.clientHeight;
    var ratio = (box.scrollHeight - box.clientHeight) / Math.max(1, track - thumb.offsetHeight);
    var startY = e.clientY;
    var startTop = box.scrollTop;
    /* Listening on window rather than capturing the pointer keeps the drag alive when the pointer
       leaves the 10px pill, and preventDefault stops the drag from selecting table text. */
    e.preventDefault();
    function move(ev) { box.scrollTop = startTop + (ev.clientY - startY) * ratio; }
    function up() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  function bindSbars() {
    var bars = document.querySelectorAll('[data-wplc-sbar]');
    for (var i = 0; i < bars.length; i++) {
      if (!bars[i].__wplcSbar) bars[i].__wplcSbar = new Sbar(bars[i]);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindSbars);
  else bindSbars();
  document.addEventListener('shopify:section:load', bindSbars);

  /* The same rail-end re-read on resize / rotation while a Note Pro Flow dialog is open. */
  window.addEventListener('resize', function () {
    var scopes = document.querySelectorAll('.ww-plans--notepro-flow ww-plans-compare');
    for (var i = 0; i < scopes.length; i++) syncRailNav(scopes[i]);
  });
  /* Overlay scrollbar for the desktop table - DTC-1346 - End */
})();
